// ============================================================================
// Gmail — capability glue.
//
// Bridges the provider-specific helpers (`send.ts`, `list.ts`, `get.ts`,
// `attachments.ts`, `watch.ts`) into the shape the capability registry
// expects (`AdapterMethods<'email'>`).
//
// The caller (main agent, at module init) will:
//   import { buildGmailMethods } from './capability-methods'
//   register('gmail', 'email', buildGmailMethods({}))
//
// The `ctx` argument on each method is the `AdapterContext` the dispatcher
// hands in — carrying the decrypted access token + per-instance config JSON.
// Gmail-specific config (e.g. `sender`) lives on `ctx.config`; we unpack
// it per call to avoid coupling the registry to Gmail specifics.
// ============================================================================

import type { AdapterMethods } from '../../services/capability-registry'
import type { EmailAddress } from '../../capabilities/email'
import { sendGmailMessage } from './send'
import { listGmailMessages } from './list'
import { getGmailMessage, markGmailAsRead } from './get'
import { getGmailAttachment } from './attachments'
import { subscribeGmailInbox, unsubscribeGmailInbox } from './watch'

// ============================================================================
// Dependencies
// ============================================================================

export interface GmailMethodsDeps {
  /**
   * Injectable fetch for tests. Defaults to the global `fetch`. Keep this as
   * the ONLY moving part so registration in production stays trivial.
   */
  fetchImpl?: typeof fetch
  /**
   * Optional fallback sender address. Per-instance config can override via
   * `ctx.config.sender_email` + `ctx.config.sender_name`; this is only used
   * when the instance config is empty (e.g. freshly-connected instance).
   */
  sender?: EmailAddress
  /**
   * Optional logger sink for non-fatal errors. Defaults to
   * `{ error: console.error }` so adapters work out of the box.
   */
  logger?: { error: (msg: string, extra?: Record<string, unknown>) => void }
}

// ============================================================================
// buildGmailMethods
// ============================================================================

export function buildGmailMethods(
  deps: GmailMethodsDeps = {},
): AdapterMethods<'email'> {
  const fetchImpl = deps.fetchImpl ?? fetch
  const fallbackSender = deps.sender
  // Issue #254 M4: the dead `_logger` variable was removed. `deps.logger`
  // remains on the public interface for when the adapter starts emitting
  // telemetry, but we no longer allocate an unused closure on every build.

  return {
    async sendEmail(args, ctx) {
      const sender = resolveSender(ctx.config, fallbackSender)
      const result = await sendGmailMessage({
        accessToken: ctx.accessToken,
        input: args,
        sender,
        fetchImpl,
      })
      return { messageId: result.messageId, threadId: result.threadId }
    },

    async listInbox(args, ctx) {
      return listGmailMessages({
        accessToken: ctx.accessToken,
        pageToken: args.pageToken,
        limit: args.limit,
        query: args.query,
        fetchImpl,
      })
    },

    async getEmail(args, ctx) {
      return getGmailMessage({
        accessToken: ctx.accessToken,
        messageId: args.messageId,
        fetchImpl,
      })
    },

    async getAttachment(args, ctx) {
      return getGmailAttachment({
        accessToken: ctx.accessToken,
        messageId: args.messageId,
        attachmentId: args.attachmentId,
        fetchImpl,
      })
    },

    async markAsRead(args, ctx) {
      await markGmailAsRead({
        accessToken: ctx.accessToken,
        messageId: args.messageId,
        fetchImpl,
      })
    },

    async subscribeInbox(args, ctx) {
      return subscribeGmailInbox({
        accessToken: ctx.accessToken,
        pubsubTopic: args.pubsubTopic,
        fetchImpl,
      })
    },

    async unsubscribeInbox(_args, ctx) {
      await unsubscribeGmailInbox({
        accessToken: ctx.accessToken,
        fetchImpl,
      })
    },
  }
}

// ============================================================================
// sender resolution — prefer per-instance config, fall back to deps.sender
// ============================================================================

function resolveSender(
  config: unknown,
  fallback: EmailAddress | undefined,
): EmailAddress | undefined {
  if (config && typeof config === 'object') {
    const cfg = config as Record<string, unknown>
    const email = typeof cfg.sender_email === 'string' ? cfg.sender_email : undefined
    const name = typeof cfg.sender_name === 'string' ? cfg.sender_name : undefined
    if (email && email.length > 0) {
      return name ? { email, name } : { email }
    }
  }
  return fallback
}
